import {init, captureException, withScope, setUser} from '@sentry/browser';
import type {BrowserOptions} from '@sentry/browser';
import {reactRouterV6BrowserTracingIntegration} from '@sentry/react';
import {useEffect} from 'react';
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from 'react-router-native';

import type {Logger, SentryLoggerFactory} from '../Logger';

const IGNORED_BOOTSTRAP_ERRORS = [
  'top.GLOBALS',
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  "Can't find variable: ZiteReader",
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',
  'fb_xd_fragment',
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  'conduitPage',
  'Script error.',
  '_avast_submit',
];

const DENIED_URLS = [
  /pagead\/js/i,
  /graph\.facebook\.com/i,
  /connect\.facebook\.net\/en_US\/all\.js/i,
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  /extensions\//i,
  /^chrome:\/\//i,
  /127\.0\.0\.1:4001\/isrunning/i,
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
];

const sentryLogger: SentryLoggerFactory = () => {
  let initialized = false;

  const initClient: Logger['init'] = ({dsn, release, environment, ignoredErrors = [], beforeSend = event => event}) => {
    if (!dsn || initialized) return;

    initialized = true;

    init({
      dsn,
      release,
      environment,
      debug: environment !== 'PRODUCTION',
      defaultIntegrations: false,
      integrations: [
        reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      tracesSampleRate: 0.5,
      allowUrls: [/.*lookiero.*/],
      ignoreErrors: [...IGNORED_BOOTSTRAP_ERRORS, ...ignoredErrors],
      denyUrls: DENIED_URLS,
      beforeSend: beforeSend as BrowserOptions['beforeSend'],
    });
  };

  const captureExceptionWithTags: Logger['captureException'] = ({error, layer, section}) => {
    withScope(scope => {
      scope.setTag('section', section);
      scope.setTag('layer', layer);
      captureException(error);
    });
  };

  const setUserContext: Logger['setUser'] = user => setUser({user});

  return {
    init: initClient,
    captureException: captureExceptionWithTags,
    setUser: setUserContext,
  };
};

export {sentryLogger};
